import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, MessageSquare, Users, HandCoins, Newspaper, LogOut, Plus, Trash2, Pencil, X, ImagePlus, Loader2, Briefcase } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { RichEditor } from "@/components/admin/RichEditor";
import api, { formatApiError, resolveImage } from "@/lib/api";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "volunteers", label: "Volunteers", icon: Users },
  { id: "donations", label: "Donations", icon: HandCoins },
  { id: "careers", label: "Careers", icon: Briefcase },
  { id: "posts", label: "Journal", icon: Newspaper },
];

const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const Table = ({ head, rows, testid }) => (
  <div className="border border-forest/20 overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-forest text-bone">
          {head.map((h) => <th key={h} className="text-left font-bold px-4 py-3 text-xs tracking-wider uppercase whitespace-nowrap">{h}</th>)}
        </tr>
      </thead>
      <tbody className="divide-y divide-forest/10">
        {rows.length === 0 ? (
          <tr><td colSpan={head.length} className="px-4 py-8 text-center text-moss italic font-serif">Nothing here yet.</td></tr>
        ) : rows}
      </tbody>
    </table>
  </div>
);

const EMPTY_POST = { title: "", excerpt: "", content: "", cover_image: "", published: true };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [contacts, setContacts] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [donations, setDonations] = useState([]);
  const [applications, setApplications] = useState([]);
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [postForm, setPostForm] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [decidingId, setDecidingId] = useState(null);
  const [showOfflineForm, setShowOfflineForm] = useState(false);
  const [offlineForm, setOfflineForm] = useState({ name: "", email: "", amount: "", phone: "", pan: "", note: "" });
  const [offlineBusy, setOfflineBusy] = useState(false);

  const recordOffline = async (e) => {
    e.preventDefault();
    setOfflineBusy(true);
    try {
      const { data } = await api.post("/admin/donations/offline", {
        ...offlineForm,
        amount: Number(offlineForm.amount),
        pan: offlineForm.pan.trim().toUpperCase() || undefined,
        phone: offlineForm.phone.trim() || undefined,
      });
      toast.success(`Gift recorded — receipt ${data.receipt_no} emailed to the donor.`);
      setOfflineForm({ name: "", email: "", amount: "", phone: "", pan: "", note: "" });
      setShowOfflineForm(false);
      const { data: fresh } = await api.get("/admin/donations");
      setDonations(fresh);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setOfflineBusy(false);
    }
  };

  const decideApplication = async (application, status) => {
    setDecidingId(application.id);
    try {
      await api.post(`/admin/applications/${application.id}/status`, { status });
      setApplications((apps) => apps.map((a) => (a.id === application.id ? { ...a, status } : a)));
      toast.success(status === "shortlisted" ? `${application.name} shortlisted — email sent.` : `${application.name} politely declined — email sent.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDecidingId(null);
    }
  };

  const resendReceipt = async (donation) => {
    setResendingId(donation.id);
    try {
      await api.post(`/admin/receipts/${donation.id}/resend`);
      toast.success(`Receipt resent to ${donation.email}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResendingId(null);
    }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10 MB.");
      return;
    }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: res } = await api.post("/admin/uploads", data);
      setPostForm((f) => ({ ...f, cover_image: res.url }));
      toast.success("Cover uploaded.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const load = () => {
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/admin/volunteers").then((r) => setVolunteers(r.data)).catch(() => {});
    api.get("/admin/donations").then((r) => setDonations(r.data)).catch(() => {});
    api.get("/admin/applications").then((r) => setApplications(r.data)).catch(() => {});
    api.get("/admin/posts").then((r) => setPosts(r.data)).catch(() => {});
  };

  useEffect(load, []);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const savePost = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/posts/${editing.id}`, postForm);
      else await api.post("/admin/posts", postForm);
      toast.success(editing ? "Story updated." : "Story published.");
      setEditing(null);
      setPostForm(EMPTY_POST);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/posts/${p.id}`);
      toast.success("Story deleted.");
      if (editing?.id === p.id) { setEditing(null); setPostForm(EMPTY_POST); }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const startEdit = (p) => {
    setEditing(p);
    setPostForm({ title: p.title, excerpt: p.excerpt, content: p.content, cover_image: p.cover_image || "", published: p.published });
  };

  const counted = donations.filter((d) => d.status !== "initiated");
  const totalINR = counted.filter((d) => (d.currency || "INR").toUpperCase() === "INR").reduce((s, d) => s + (d.amount || 0), 0);
  const totalUSD = counted.filter((d) => (d.currency || "INR").toUpperCase() !== "INR").reduce((s, d) => s + (d.amount || 0), 0);
  const totalLabel = [totalINR ? `₹${totalINR.toLocaleString("en-IN")}` : "", totalUSD ? `$${totalUSD.toLocaleString()}` : ""].filter(Boolean).join(" + ") || "₹0";

  return (
    <div className="min-h-screen bg-bone flex" data-testid="admin-dashboard">
      <aside className="w-16 lg:w-60 bg-forest text-bone flex flex-col shrink-0 min-h-screen sticky top-0" data-testid="admin-sidebar">
        <div className="px-4 lg:px-6 h-[72px] flex items-center gap-3 border-b border-bone/15">
          <span className="bg-bone p-1 inline-flex shrink-0">
            <img src="/logo-mark.png" alt="Learning Lights Initiative logo" className="h-7 w-auto" />
          </span>
          <span className="font-sans font-extrabold text-sm uppercase tracking-wide hidden lg:block">LLI Admin</span>
        </div>
        <nav className="flex-1 py-6 flex flex-col gap-1 px-2 lg:px-3">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-3 px-3 py-3 text-sm font-semibold transition-colors duration-150 ${tab === t.id ? "bg-terra text-bone" : "text-bone/60 hover:text-bone hover:bg-bone/10"}`}>
              <t.icon size={18} />
              <span className="hidden lg:inline">{t.label}</span>
            </button>
          ))}
        </nav>
        <button onClick={doLogout} data-testid="admin-logout-btn"
          className="m-3 flex items-center gap-3 px-3 py-3 text-sm font-semibold text-bone/60 hover:text-bone hover:bg-bone/10 transition-colors">
          <LogOut size={18} />
          <span className="hidden lg:inline">Log out</span>
        </button>
      </aside>

      <main className="flex-1 p-6 lg:p-10 min-w-0">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-serif font-bold tracking-tighter text-forest text-3xl capitalize" data-testid="admin-heading">{TABS.find(t => t.id === tab)?.label}</h1>
          <span className="text-sm text-moss" data-testid="admin-user-badge">{user?.email}</span>
        </div>

        {tab === "overview" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="admin-overview">
            {[
              { label: "Contact messages", value: contacts.length },
              { label: "Volunteer signups", value: volunteers.length },
              { label: "Career applications", value: applications.length },
              { label: "Donations", value: donations.length },
              { label: "Donation total", value: totalLabel },
            ].map((s, i) => (
              <div key={s.label} className="border border-forest/20 bg-bone p-6" data-testid={`admin-stat-${i}`}>
                <p className="font-serif font-bold text-4xl text-forest tracking-tighter">{s.value}</p>
                <p className="mt-2 text-xs font-bold uppercase tracking-wider text-moss">{s.label}</p>
              </div>
            ))}
            <div className="sm:col-span-2 lg:col-span-5 border border-forest/20 bg-bonealt/60 p-6">
              <p className="text-sm text-moss">
                Journal stories live: <strong className="text-forest">{posts.filter(p => p.published).length}</strong> ·
                Drafts: <strong className="text-forest">{posts.filter(p => !p.published).length}</strong>
              </p>
            </div>
          </div>
        )}

        {tab === "messages" && (
          <Table testid="admin-messages-table" head={["Date", "Name", "Email", "Subject", "Message"]}
            rows={contacts.map((c) => (
              <tr key={c.id} className="hover:bg-bonealt/60 align-top" data-testid={`admin-message-row-${c.id}`}>
                <td className="px-4 py-3 whitespace-nowrap text-moss">{fmtDate(c.created_at)}</td>
                <td className="px-4 py-3 font-semibold text-forest">{c.name}</td>
                <td className="px-4 py-3 text-moss">{c.email}</td>
                <td className="px-4 py-3 text-moss">{c.subject || "—"}</td>
                <td className="px-4 py-3 text-moss max-w-md"><span className="line-clamp-2">{c.message}</span></td>
              </tr>
            ))} />
        )}

        {tab === "volunteers" && (
          <Table testid="admin-volunteers-table" head={["Date", "Name", "Email", "Phone", "Interest", "Note"]}
            rows={volunteers.map((v) => (
              <tr key={v.id} className="hover:bg-bonealt/60 align-top" data-testid={`admin-volunteer-row-${v.id}`}>
                <td className="px-4 py-3 whitespace-nowrap text-moss">{fmtDate(v.created_at)}</td>
                <td className="px-4 py-3 font-semibold text-forest">{v.name}</td>
                <td className="px-4 py-3 text-moss">{v.email}</td>
                <td className="px-4 py-3 text-moss">{v.phone || "—"}</td>
                <td className="px-4 py-3"><span className="bg-forest text-bone text-xs font-bold px-2 py-1">{v.interest}</span></td>
                <td className="px-4 py-3 text-moss max-w-xs"><span className="line-clamp-2">{v.message || "—"}</span></td>
              </tr>
            ))} />
        )}

        {tab === "donations" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button onClick={() => setShowOfflineForm(!showOfflineForm)} data-testid="offline-donation-toggle"
                className="flex items-center gap-2 bg-forest text-bone text-sm font-bold px-4 py-2.5 hover:bg-terra transition-colors">
                {showOfflineForm ? <X size={15} /> : <Plus size={15} />} Record QR / offline gift
              </button>
            </div>
            {showOfflineForm && (
              <form onSubmit={recordOffline} className="border border-forest/20 bg-bonealt/50 p-6 space-y-4" data-testid="offline-donation-form">
                <p className="text-sm text-moss">For donors who paid via your UPI QR or bank transfer. The 80G receipt is generated and emailed to them immediately.</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  <input required placeholder="Donor name" value={offlineForm.name} onChange={(e) => setOfflineForm({ ...offlineForm, name: e.target.value })}
                    className="bg-bone border border-forest/25 px-4 py-3 text-sm focus:outline-none focus:border-terra transition" data-testid="offline-name-input" />
                  <input required type="email" placeholder="Donor email" value={offlineForm.email} onChange={(e) => setOfflineForm({ ...offlineForm, email: e.target.value })}
                    className="bg-bone border border-forest/25 px-4 py-3 text-sm focus:outline-none focus:border-terra transition" data-testid="offline-email-input" />
                  <input required type="number" min="1" placeholder="Amount (₹)" value={offlineForm.amount} onChange={(e) => setOfflineForm({ ...offlineForm, amount: e.target.value })}
                    className="bg-bone border border-forest/25 px-4 py-3 text-sm focus:outline-none focus:border-terra transition" data-testid="offline-amount-input" />
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <input type="tel" placeholder="Phone (optional)" value={offlineForm.phone} onChange={(e) => setOfflineForm({ ...offlineForm, phone: e.target.value })}
                    className="bg-bone border border-forest/25 px-4 py-3 text-sm focus:outline-none focus:border-terra transition" data-testid="offline-phone-input" />
                  <input placeholder="PAN (optional, for 80G)" value={offlineForm.pan} onChange={(e) => setOfflineForm({ ...offlineForm, pan: e.target.value.toUpperCase().slice(0, 10) })}
                    className="bg-bone border border-forest/25 px-4 py-3 text-sm uppercase tracking-widest focus:outline-none focus:border-terra transition" data-testid="offline-pan-input" />
                  <input placeholder="Note (optional)" value={offlineForm.note} onChange={(e) => setOfflineForm({ ...offlineForm, note: e.target.value })}
                    className="bg-bone border border-forest/25 px-4 py-3 text-sm focus:outline-none focus:border-terra transition" data-testid="offline-note-input" />
                </div>
                <button type="submit" disabled={offlineBusy} data-testid="offline-submit-btn"
                  className="bg-terra text-bone font-bold px-8 py-3 hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:opacity-60 disabled:translate-y-0 flex items-center gap-2">
                  {offlineBusy && <Loader2 size={15} className="animate-spin" />}
                  {offlineBusy ? "Recording…" : "Record gift & send 80G receipt"}
                </button>
              </form>
            )}
            <Table testid="admin-donations-table" head={["Date", "Name", "Email", "Amount", "Frequency", "Status", "Receipt"]}
            rows={donations.map((d) => (
              <tr key={d.id} className="hover:bg-bonealt/60" data-testid={`admin-donation-row-${d.id}`}>
                <td className="px-4 py-3 whitespace-nowrap text-moss">{fmtDate(d.created_at)}</td>
                <td className="px-4 py-3 font-semibold text-forest">{d.name}</td>
                <td className="px-4 py-3 text-moss">{d.email}</td>
                <td className="px-4 py-3 font-serif font-bold text-terra">{(d.currency || "INR").toUpperCase() === "INR" ? "₹" : "$"}{d.amount.toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 text-moss capitalize">{d.frequency}</td>
                <td className="px-4 py-3"><span className="bg-bonealt border border-forest/20 text-forest text-xs font-bold px-2 py-1 capitalize">{d.status}</span></td>
                <td className="px-4 py-3">
                  {(d.status === "paid" || d.status === "active") ? (
                    <span className="flex items-center gap-3">
                      <a href={resolveImage(`/api/receipts/${d.id}`)} target="_blank" rel="noopener noreferrer" className="text-terra font-bold text-xs hover:underline" data-testid={`admin-receipt-${d.id}`}>80G receipt</a>
                      <button onClick={() => resendReceipt(d)} disabled={resendingId === d.id} data-testid={`admin-resend-${d.id}`}
                        className="text-xs font-bold text-forest hover:text-terra transition-colors disabled:opacity-50 flex items-center gap-1">
                        {resendingId === d.id && <Loader2 size={11} className="animate-spin" />} Resend
                      </button>
                    </span>
                  ) : <span className="text-moss text-xs">—</span>}
                </td>
              </tr>
            ))} />
          </div>
        )}

        {tab === "careers" && (
          <Table testid="admin-applications-table" head={["Date", "Name", "Contact", "Role", "Resume", "Message", "Decision"]}
            rows={applications.map((a) => (
              <tr key={a.id} className="hover:bg-bonealt/60" data-testid={`admin-application-row-${a.id}`}>
                <td className="px-4 py-3 whitespace-nowrap text-moss">{fmtDate(a.created_at)}</td>
                <td className="px-4 py-3 font-semibold text-forest">{a.name}</td>
                <td className="px-4 py-3 text-moss">{a.email}<br />{a.phone}</td>
                <td className="px-4 py-3 text-forest">{a.role} <span className="text-xs text-moss">({a.role_type})</span></td>
                <td className="px-4 py-3">
                  {a.resume_path
                    ? <a href={resolveImage(`/api/files/${a.resume_path}`)} target="_blank" rel="noopener noreferrer" className="text-terra font-bold text-xs hover:underline" data-testid={`admin-resume-${a.id}`}>View resume</a>
                    : <span className="text-moss text-xs">—</span>}
                </td>
                <td className="px-4 py-3 text-moss max-w-xs truncate">{a.message || "—"}</td>
                <td className="px-4 py-3">
                  {a.status === "shortlisted" ? (
                    <span className="bg-forest text-bone text-xs font-bold px-2 py-1" data-testid={`admin-decision-${a.id}`}>Shortlisted</span>
                  ) : a.status === "rejected" ? (
                    <span className="bg-bonealt border border-forest/20 text-moss text-xs font-bold px-2 py-1" data-testid={`admin-decision-${a.id}`}>Declined</span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <button onClick={() => decideApplication(a, "shortlisted")} disabled={decidingId === a.id} data-testid={`admin-shortlist-${a.id}`}
                        className="bg-forest text-bone text-xs font-bold px-2.5 py-1.5 hover:bg-terra transition-colors disabled:opacity-50 flex items-center gap-1">
                        {decidingId === a.id && <Loader2 size={10} className="animate-spin" />} Shortlist
                      </button>
                      <button onClick={() => decideApplication(a, "rejected")} disabled={decidingId === a.id} data-testid={`admin-decline-${a.id}`}
                        className="border border-forest/25 text-moss text-xs font-bold px-2.5 py-1.5 hover:border-terra hover:text-terra transition-colors disabled:opacity-50">
                        Decline
                      </button>
                    </span>
                  )}
                </td>
              </tr>
            ))} />
        )}

        {tab === "posts" && (
          <div className="grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3 space-y-3" data-testid="admin-posts-list">
              <button onClick={() => { setEditing(null); setPostForm(EMPTY_POST); }} data-testid="admin-new-post-btn"
                className="flex items-center gap-2 bg-terra text-bone text-sm font-bold px-5 py-3 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200">
                <Plus size={16} /> New Story
              </button>
              {posts.length === 0 && <p className="text-moss italic font-serif pt-4">No stories yet.</p>}
              {posts.map((p) => (
                <div key={p.id} className={`border p-4 flex items-center gap-4 ${editing?.id === p.id ? "border-terra bg-bonealt/60" : "border-forest/20"}`} data-testid={`admin-post-row-${p.slug}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif font-bold text-forest truncate">{p.title}</p>
                    <p className="text-xs text-moss mt-1">
                      {fmtDate(p.created_at)} · {p.published ? <span className="text-forest font-bold">Published</span> : <span className="text-terra font-bold">Draft</span>}
                    </p>
                  </div>
                  <button onClick={() => startEdit(p)} data-testid={`admin-post-edit-${p.slug}`} className="p-2 text-forest hover:text-terra transition-colors" aria-label="Edit"><Pencil size={16} /></button>
                  <button onClick={() => deletePost(p)} data-testid={`admin-post-delete-${p.slug}`} className="p-2 text-forest hover:text-destructive transition-colors" aria-label="Delete"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>

            <form onSubmit={savePost} className="lg:col-span-2 border border-forest/20 p-6 space-y-4 bg-bone self-start sticky top-6" data-testid="admin-post-form">
              <div className="flex items-center justify-between">
                <h3 className="font-serif font-bold text-forest text-xl">{editing ? "Edit story" : "New story"}</h3>
                {editing && (
                  <button type="button" onClick={() => { setEditing(null); setPostForm(EMPTY_POST); }} data-testid="admin-post-cancel-edit" className="text-moss hover:text-terra"><X size={16} /></button>
                )}
              </div>
              <input required placeholder="Title" value={postForm.title} onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                className="w-full bg-bone border border-forest/25 px-4 py-3 text-sm focus:outline-none focus:border-terra transition" data-testid="post-title-input" />
              <input placeholder="Excerpt (shown in cards)" value={postForm.excerpt} onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })}
                className="w-full bg-bone border border-forest/25 px-4 py-3 text-sm focus:outline-none focus:border-terra transition" data-testid="post-excerpt-input" />
              <div className="space-y-2">
                <span className="text-xs font-bold uppercase tracking-wider text-moss">Cover image</span>
                {postForm.cover_image && (
                  <div className="relative border border-forest/20">
                    <img src={resolveImage(postForm.cover_image)} alt="Cover preview" className="w-full h-32 object-cover" data-testid="post-cover-preview" />
                    <button type="button" onClick={() => setPostForm({ ...postForm, cover_image: "" })} data-testid="post-cover-remove"
                      className="absolute top-2 right-2 bg-forest/85 text-bone p-1.5 hover:bg-destructive transition-colors" aria-label="Remove cover"><X size={14} /></button>
                  </div>
                )}
                <label className={`flex items-center justify-center gap-2 border border-dashed border-forest/30 px-4 py-3 text-sm font-bold text-forest cursor-pointer hover:border-terra hover:text-terra transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="post-cover-upload-label">
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
                  {uploading ? "Uploading…" : postForm.cover_image ? "Replace photo" : "Upload a photo"}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadCover} data-testid="post-cover-file-input" />
                </label>
                <input placeholder="…or paste an image URL" value={postForm.cover_image.startsWith("/api/") ? "" : postForm.cover_image} onChange={(e) => setPostForm({ ...postForm, cover_image: e.target.value })}
                  className="w-full bg-bone border border-forest/25 px-4 py-2.5 text-xs focus:outline-none focus:border-terra transition" data-testid="post-cover-input" />
              </div>
              <RichEditor value={postForm.content} onChange={(html) => setPostForm((f) => ({ ...f, content: html }))} />
              <label className="flex items-center gap-3 text-sm font-semibold text-forest cursor-pointer">
                <input type="checkbox" checked={postForm.published} onChange={(e) => setPostForm({ ...postForm, published: e.target.checked })} data-testid="post-published-toggle" className="accent-terra w-4 h-4" />
                Published (visible on the site)
              </label>
              <button type="submit" disabled={saving} data-testid="post-save-btn"
                className="w-full bg-forest text-bone font-bold py-3 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 disabled:opacity-60">
                {saving ? "Saving…" : editing ? "Save Changes" : "Publish Story"}
              </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
